import {
  isQuestionnaireItemEnabled,
  resolveLocalizedString,
  resolveQuestionnaireItemLabel,
  type QuestionnaireItemAnswerOptionWithTranslations,
  type QuestionnaireItemLabelSource,
} from '@liza/fhir-utils';
import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '../atoms/DatePicker';
import { Checkbox } from '../ui/checkbox';
import { FieldLabel } from '../ui/field';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';

export type AoeQuestionnaireFieldsProps = {
  /** The (normalized) AOE questionnaire to render one input per item for. */
  questionnaire: Questionnaire;
  /** The in-progress response, or `undefined` before any answer is given. */
  response: QuestionnaireResponse | undefined;
  /** Called with the full updated response whenever any answer changes. */
  onChange: (response: QuestionnaireResponse) => void;
  /** linkIds of required items that are still unanswered — rendered with an error treatment. */
  invalidLinkIds?: ReadonlySet<string>;
  /**
   * `inline` (default) — label beside control, for compact AOE rows.
   * `stacked` — label above control, for patient-record / dialog forms.
   */
  layout?: 'inline' | 'stacked';
  /** Optional max length for `string` / `text` answers. */
  maxTextLength?: number;
  /** Prefix for control ids (defaults to `aoe`). */
  idPrefix?: string;
  /**
   * `clinical` (default) — `item.text` (admin/chart).
   * `patient` — patient-facing extension when present, else clinical.
   */
  labelSource?: QuestionnaireItemLabelSource;
};

const firstAnswer = (
  response: QuestionnaireResponse | undefined,
  linkId: string,
): QuestionnaireResponseItemAnswer | undefined => response?.item?.find((i) => i.linkId === linkId)?.answer?.[0];

/**
 * Upsert a single item's answer into the response, preserving the other items. Answers are stored
 * flat (one top-level `item` per linkId) even for items nested under a `group`; `firstAnswer` reads
 * them back the same way, so the round-trip is self-consistent. This deliberately flattens the FHIR
 * nesting — keep reader and writer in sync if that ever needs to change.
 */
const upsertAnswer = (
  response: QuestionnaireResponse | undefined,
  questionnaire: Questionnaire,
  item: QuestionnaireItem,
  answer: QuestionnaireResponseItemAnswer | undefined,
): QuestionnaireResponse => {
  const base: QuestionnaireResponse = response ?? {
    resourceType: 'QuestionnaireResponse',
    status: 'in-progress',
    ...(questionnaire.url ? { questionnaire: questionnaire.url } : {}),
  };
  const items = (base.item ?? []).filter((i) => i.linkId !== item.linkId);
  if (answer) items.push({ linkId: item.linkId, text: item.text, answer: [answer] });
  return { ...base, item: items };
};

const optionValue = (option: NonNullable<QuestionnaireItem['answerOption']>[number]): string =>
  option.valueCoding?.code ?? option.valueString ?? '';

const optionLabel = (option: NonNullable<QuestionnaireItem['answerOption']>[number], locale: string): string => {
  if (option.valueCoding) {
    const translated = option as QuestionnaireItemAnswerOptionWithTranslations;
    return (
      resolveLocalizedString(option.valueCoding.display, translated.valueCoding?._display, locale) ??
      option.valueCoding.display ??
      option.valueCoding.code ??
      ''
    );
  }
  return option.valueString ?? '';
};

/** FHIR extension HG uses to declare the unit for a `quantity` questionnaire item. */
const QUESTIONNAIRE_UNIT_URL = 'http://hl7.org/fhir/StructureDefinition/questionnaire-unit';

type ControlProps = {
  item: QuestionnaireItem;
  current: QuestionnaireResponseItemAnswer | undefined;
  invalid: boolean;
  label: string;
  id: string;
  locale: string;
  maxTextLength?: number;
  onAnswer: (answer: QuestionnaireResponseItemAnswer | undefined) => void;
};

const renderNumberControl = ({ item, current, invalid, label, id, onAnswer }: ControlProps): React.ReactNode => {
  const isQuantity = item.type === 'quantity';
  const value = (isQuantity ? current?.valueQuantity?.value : (current?.valueDecimal ?? current?.valueInteger)) ?? '';
  // HG declares a quantity item's unit via the `questionnaire-unit` extension; carry it onto the
  // answer so the unit isn't dropped (some labs reject unit-less quantities).
  const unit = item.extension?.find((e) => e.url === QUESTIONNAIRE_UNIT_URL)?.valueCoding;
  const unitLabel = unit?.display ?? unit?.code;
  return (
    <Input
      id={id}
      type="number"
      value={value}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') return onAnswer(undefined);
        if (isQuantity)
          return onAnswer({
            valueQuantity: {
              value: Number.parseFloat(raw),
              ...(unitLabel && { unit: unitLabel }),
              ...(unit?.system && { system: unit.system }),
              ...(unit?.code && { code: unit.code }),
            },
          });
        if (item.type === 'integer') return onAnswer({ valueInteger: Number.parseInt(raw, 10) });
        onAnswer({ valueDecimal: Number.parseFloat(raw) });
      }}
      aria-label={label}
      aria-invalid={invalid}
      aria-required={item.required ?? false}
    />
  );
};

const renderChoiceControl = ({
  item,
  current,
  invalid,
  label,
  id,
  locale,
  onAnswer,
}: ControlProps): React.ReactNode => {
  const options = (item.answerOption ?? []).filter((o) => optionValue(o) !== '');
  return (
    <Select
      value={current?.valueCoding?.code ?? current?.valueString ?? ''}
      onValueChange={(value) => {
        const option = options.find((o) => optionValue(o) === value);
        if (!option) return onAnswer(undefined);
        onAnswer(option.valueCoding ? { valueCoding: option.valueCoding } : { valueString: option.valueString });
      }}
    >
      <SelectTrigger
        id={id}
        className="w-full"
        aria-label={label}
        aria-invalid={invalid}
        aria-required={item.required ?? false}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={optionValue(option)} value={optionValue(option)}>
            {optionLabel(option, locale)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const renderStringControl = ({
  item,
  current,
  invalid,
  label,
  id,
  maxTextLength,
  onAnswer,
}: ControlProps): React.ReactNode => {
  const onText = (text: string): void => {
    const capped = maxTextLength !== undefined ? text.slice(0, maxTextLength) : text;
    onAnswer(capped ? { valueString: capped } : undefined);
  };
  if (item.type === 'text') {
    return (
      <Textarea
        id={id}
        value={current?.valueString ?? ''}
        onChange={(e) => onText(e.target.value)}
        aria-label={label}
        aria-invalid={invalid}
        aria-required={item.required ?? false}
        maxLength={maxTextLength}
        rows={3}
      />
    );
  }
  return (
    <Input
      id={id}
      value={current?.valueString ?? ''}
      onChange={(e) => onText(e.target.value)}
      aria-label={label}
      aria-invalid={invalid}
      aria-required={item.required ?? false}
      maxLength={maxTextLength}
    />
  );
};

/** Render the input control for a single (non-group, non-display) AOE item. */
function renderControl(props: ControlProps): React.ReactNode {
  const { item, current, invalid, label, id, onAnswer } = props;
  switch (item.type) {
    case 'boolean':
      return (
        <Checkbox
          id={id}
          checked={current?.valueBoolean ?? false}
          onCheckedChange={(checked) => onAnswer(checked === true ? { valueBoolean: true } : undefined)}
          aria-label={label}
          aria-required={item.required ?? false}
        />
      );
    case 'date':
      return (
        <DatePicker
          id={id}
          value={current?.valueDate ?? ''}
          onChange={(value) => onAnswer(value ? { valueDate: value } : undefined)}
          aria-label={label}
          aria-invalid={invalid}
          aria-required={item.required ?? false}
        />
      );
    case 'integer':
    case 'decimal':
    case 'quantity':
      return renderNumberControl(props);
    case 'choice':
    case 'open-choice':
      return renderChoiceControl(props);
    default:
      return renderStringControl(props);
  }
}

/**
 * Drop answers for items whose `enableWhen` is no longer satisfied (e.g. clearing “Other” free text
 * when the parent choice changes away from `other`).
 */
const pruneDisabledAnswers = (questionnaire: Questionnaire, response: QuestionnaireResponse): QuestionnaireResponse => {
  const enabledLinkIds = new Set<string>();
  const walk = (items: QuestionnaireItem[] | undefined): void => {
    for (const item of items ?? []) {
      if (!isQuestionnaireItemEnabled(item, response)) continue;
      if (item.type === 'group') {
        walk(item.item);
        continue;
      }
      if (item.type === 'display') continue;
      enabledLinkIds.add(item.linkId);
    }
  };
  walk(questionnaire.item);
  return {
    ...response,
    item: (response.item ?? []).filter((item) => enabledLinkIds.has(item.linkId)),
  };
};

const itemLabel = (item: QuestionnaireItem, locale: string, labelSource: QuestionnaireItemLabelSource): string =>
  resolveQuestionnaireItemLabel(item, locale, labelSource);

/**
 * Renders editable inputs for a `Questionnaire`, one control per item keyed by item type
 * (string/text/boolean/number/quantity/date/choice). Pure and controlled: it reads answers from
 * `response` and emits a fully-rebuilt `QuestionnaireResponse` on every change. Required-item
 * validation is the caller's job — pass unanswered required linkIds as `invalidLinkIds`.
 * Items with unsatisfied `enableWhen` are hidden and their answers pruned on change.
 */
export const AoeQuestionnaireFields = ({
  questionnaire,
  response,
  onChange,
  invalidLinkIds,
  layout = 'inline',
  maxTextLength,
  idPrefix = 'aoe',
  labelSource = 'clinical',
}: AoeQuestionnaireFieldsProps): React.ReactElement => {
  const { t, i18n } = useTranslation('records');
  const stacked = layout === 'stacked';
  const locale = i18n.language;

  const renderItem = (item: QuestionnaireItem): React.ReactNode => {
    if (!isQuestionnaireItemEnabled(item, response)) return null;

    if (item.type === 'group') {
      const groupLabel = itemLabel(item, locale, labelSource);
      return (
        <fieldset key={item.linkId} className="space-y-3">
          {groupLabel && <legend className="text-sm font-medium">{groupLabel}</legend>}
          {item.item?.map(renderItem)}
        </fieldset>
      );
    }
    if (item.type === 'display') {
      return (
        <p key={item.linkId} className="text-sm text-muted-foreground">
          {itemLabel(item, locale, labelSource)}
        </p>
      );
    }

    const id = `${idPrefix}-${item.linkId}`;
    const label = itemLabel(item, locale, labelSource);
    const invalid = invalidLinkIds?.has(item.linkId) ?? false;
    const current = firstAnswer(response, item.linkId);
    const onAnswer = (answer: QuestionnaireResponseItemAnswer | undefined): void => {
      const next = upsertAnswer(response, questionnaire, item, answer);
      onChange(pruneDisabledAnswers(questionnaire, next));
    };
    const control = renderControl({ item, current, invalid, label, id, locale, maxTextLength, onAnswer });
    const labelEl = (
      <FieldLabel htmlFor={id} className={stacked ? undefined : 'w-20 shrink-0'}>
        {label}
        {item.required && <span aria-hidden="true"> *</span>}
      </FieldLabel>
    );

    if (item.type === 'boolean') {
      return (
        <div key={item.linkId} className={stacked ? 'space-y-2' : 'flex items-center gap-2'}>
          {labelEl}
          {control}
        </div>
      );
    }

    if (stacked) {
      return (
        <div key={item.linkId} className="space-y-2" data-invalid={invalid || undefined}>
          {labelEl}
          {control}
          {invalid && <p className="text-sm text-destructive">{t('requiredAnswer')}</p>}
        </div>
      );
    }

    return (
      <div key={item.linkId} className="space-y-1">
        <div className="flex items-center gap-2">
          {labelEl}
          <div className="flex-1 *:w-full">{control}</div>
        </div>
        {invalid && <p className="text-sm text-destructive">{t('requiredAnswer')}</p>}
      </div>
    );
  };

  return <div className="space-y-3">{(questionnaire.item ?? []).map(renderItem)}</div>;
};
